// /c/{id}: for the instructor, the class dashboard (byj-1wv.4.3) — every
// student session as a row, as /shares lists shares: those that need the
// instructor first under their own heading, then the live ones, then the
// ended; chips by state, a search by name, live through the class socket,
// and "Message all" on the head (byj-1wv.4.4). For a student, the class, its
// assignments and how to share with each, and their own shares to it
// (RFC-043 model 19) — which the instructor previews from the head.
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, wsURL, type Announcement, type ClassDetail, type ClassSession, type ShareDetail, type User } from "../api";
import { applyClassFrame, asShown, assignmentOf, counts, currentAlerts, kindCounts, matches, NoAssignment, sortSessions, States, totals, seenMark, unknownShare, type AlertFilter, type Filter, type SeenMark } from "../classroom";
import { LiveConnection, type ConnState, type SocketLike } from "../live";
import type { Envelope } from "../protocol";
import { useChooseAssignment } from "../components/ChooseAssignment";
import { AlertIcon, alertTone } from "../components/AlertIcon";
import { ClassSessionRow } from "../components/ClassSessionRow";
import { MaxPins, SessionWatch, usePins, useSeenAlerts } from "../components/SessionWatch";
import { FilterChips, type Chip } from "../components/FilterChips";
import { ListSearch } from "../components/ListSearch";
import { SelectionBar, useSelectMode } from "../components/Selection";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { Popover } from "../components/Popover";
import { Grouped, type Group } from "../components/Rows";
import { useNow } from "../now";
import { t, dueShort, money, whenMinute, word, phrases } from "../i18n";
import { dueState } from "../components/StudentWork";
import { StudentClass } from "../components/StudentClass";
import { Announcements } from "../components/Announcements";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The reason the relay closes a deleted class's socket with, 4404 (Go `classWS`, byj-56x). */
export const ClassDeletedReason = "the class was deleted";

/** The loader: the class, then the dashboard or the student's page. */
export function ClassPage({ id, me }: { id: string; me: User }) {
  const [cls, setCls] = useState<ClassDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.class(id).then(setCls, (err: unknown) => setError(errText(err)));
  }, [id]);
  if (error) {
    return (
      <PageNotice error={error}>
        <Link to="/classes">{t("classes:classesTitle")}</Link>
      </PageNotice>
    );
  }
  if (!cls) return <PageLoading />;
  if (cls.role !== "instructor") return <StudentClass cls={cls} />;
  return <Dashboard cls={cls} me={me} />;
}

/**
 * Message all: a note left in every live session, and — unless the
 * instructor says not — kept on the class's page as an announcement for
 * every student whenever they come (byj-1wv.135): a note to live sessions
 * alone reached none of an exam's students, who sit it on their own
 * machines. Under it the announcements posted, each to take down.
 */
function Broadcast({ classId, announced, onAnnounced }: { classId: string; announced: Announcement[]; onAnnounced: (a: Announcement[]) => void }) {
  const [text, setText] = useState("");
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      if (keep) {
        const r = await api.announce(classId, body);
        onAnnounced([r.announcement, ...announced]);
        setResult(t("classes:announcedDelivered", { count: r.delivered }));
      } else {
        const r = await api.broadcast(classId, body);
        setResult(t("classes:broadcastDelivered", { count: r.delivered }));
      }
      setText("");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const takeDown = async (a: Announcement) => {
    setError(null);
    setResult(null);
    try {
      await api.deleteAnnouncement(classId, a.id);
      onAnnounced(announced.filter((x) => x.id !== a.id));
    } catch (err) {
      setError(errText(err));
    }
  };
  return (
    <form className="stack broadcast" onSubmit={(e) => void send(e)}>
      <h3 className="flush">{t("classes:broadcastTitle")}</h3>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("classes:broadcastPlaceholder")}
        aria-label={t("classes:broadcastTitle")}
        maxLength={2000}
        rows={3}
      />
      <label className="row small">
        <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />
        {t("classes:keepOnClassPage")}
      </label>
      <div className="row">
        <span className="grow">
          {result && (
            <span className="ok-text small" role="status">
              {result}
            </span>
          )}
          {error && <span className="error small">{t("common:error", { m: error })}</span>}
        </span>
        <button className="primary" type="submit" disabled={busy || !text.trim()}>
          {t("classes:sendBroadcast")}
        </button>
      </div>
      <Announcements items={announced} onDelete={(a) => void takeDown(a)} />
    </form>
  );
}

/** Whether a session wants the instructor now: it raised an alert, or it waits on its student. */
const wantsAttention = (s: ClassSession) =>
  s.state !== "ended" && s.live && (s.alerts.length > 0 || s.state === "needs-input" || s.pending_permissions > 0);

export function Dashboard({
  cls,
  me,
  socket,
  load = api.classSessions,
  loadShare,
}: {
  cls: ClassDetail;
  me: User;
  /** A socket factory, for tests: the class feed's and the preview's timeline. */
  socket?: (url: string) => SocketLike;
  /** The session list, for tests. */
  load?: (id: string) => Promise<ClassSession[]>;
  /** A watched share's detail, for tests. */
  loadShare?: (id: string) => Promise<ShareDetail>;
}) {
  const [sessions, setSessions] = useState<ClassSession[] | null>(null);
  // what the instructor posted to the class, kept here so the popover shows it again
  const [announced, setAnnounced] = useState<Announcement[]>(cls.announcements ?? []);
  // the sessions watched beside the rows: the one picked last, and those
  // pinned (at most MaxPins), which stay while others are picked
  const [watching, setWatching] = useState<string | null>(null);
  const [pins, togglePin, keepPins] = usePins(cls.id);
  // several sessions picked to set one assignment to all (Select, as /shares
  // picks shares to delete); while picking, a row's press picks it
  const mode = useSelectMode();
  const press = useCallback((s: ClassSession) => (mode.selecting ? mode.toggle(s.share) : setWatching(s.share)), [mode.selecting, mode.toggle]);
  const [bulkSaid, setBulkSaid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conn, setConn] = useState<ConnState>({ kind: "connecting" });
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [assignment, setAssignment] = useState("");
  // the alerts' part of the filters: any alert, or one kind
  const [alertFilter, setAlertFilter] = useState<AlertFilter>("");
  const [seen, markSeen, keepSeen] = useSeenAlerts(cls.id);
  const [choose, chooseDialog] = useChooseAssignment();
  // Esc puts the picking away — unless something over it took the Esc first:
  // the assignment's dialog, a popover, a watch (components/Modal). Heard on
  // the window, after every listener on the document has had it, so a
  // popover opened later than the picking began still comes first
  const picking = mode.selecting;
  const stopPicking = mode.select;
  useEffect(() => {
    if (!picking) return;
    const onKey = (e: KeyboardEvent) => {
      // not an Esc in a field: the search's own clears what was typed, and only that
      if (e.target instanceof Element && e.target.closest("input, textarea")) return;
      if (e.key === "Escape" && !e.defaultPrevented) stopPicking(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [picking, stopPicking]);
  const assignments = useMemo(() => cls.assignments ?? [], [cls.assignments]);
  const openAssignments = useMemo(() => assignments.filter((a) => !a.archived_at), [assignments]);
  // how long ago, said again while the page is open (now.ts)
  const now = useNow();
  const listRef = useRef<ClassSession[] | null>(null);
  listRef.current = sessions;
  const reloading = useRef(false);
  // shares a reload asked about and did not list — a removed student's, whose
  // frames still reach the feed: not asked about again (byj-1wv.10.4)
  const unlisted = useRef(new Set<string>());

  const reload = useCallback(async (askedFor?: string) => {
    if (reloading.current) return;
    reloading.current = true;
    try {
      const list = await load(cls.id);
      setSessions(list);
      if (askedFor && !list.some((x) => x.share === askedFor)) unlisted.current.add(askedFor);
      setError(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      reloading.current = false;
    }
  }, [cls.id, load]);

  useEffect(() => {
    void reload();
    const onFrame = (e: Envelope) => {
      const list = listRef.current;
      if (list && unknownShare(list, e)) {
        if (!e.share || !unlisted.current.has(e.share)) void reload(e.share);
        return;
      }
      setSessions((cur) => (cur ? applyClassFrame(cur, e, assignments) : cur));
    };
    let opened = false;
    const c = new LiveConnection({
      url: () => wsURL(`/v1/classes/${encodeURIComponent(cls.id)}/ws`),
      me: { kind: "coach", user: me.id },
      fanIn: true,
      onFrame,
      onState: (s) => {
        setConn(s);
        // What happened while the socket was down is in the list, not the socket.
        if (s.kind === "open") {
          if (opened) void reload();
          opened = true;
        }
      },
      socket,
    });
    c.start();
    return () => c.stop("left");
  }, [cls.id, me.id, socket, reload, assignments]);

  // the instructor sets a share's assignment from its watch's bar (RFC-043 model 19)
  const setShareAssignment = useCallback(
    async (s: ClassSession) => {
      const name = t("classes:assignmentFor", { name: s.student.name });
      const key = await choose(name, s.assignment?.key ?? "", assignments);
      if (key === undefined) return;
      try {
        const v = await api.setShareAssignment(s.share, key);
        setSessions((cur) =>
          cur
            ? cur.map((x) => {
                if (x.share !== s.share) return x;
                const { assignment, late } = assignmentOf(v.assignment?.key, assignments, x.last_event_at);
                return { ...x, assignment, late };
              })
            : cur,
        );
      } catch (err) {
        setError(errText(err));
      }
    },
    [assignments, choose],
  );

  // one assignment, or none, set to every session picked, one PATCH each (the
  // relay sets a share's alone); what failed is said, the rest kept
  const setPickedAssignment = async (shares: string[]) => {
    if (shares.length === 0) return;
    const key = await choose(t("classes:assignmentForN", { count: shares.length }), "", assignments, true);
    if (key === undefined) return;
    let done = 0;
    for (const share of shares) {
      try {
        const v = await api.setShareAssignment(share, key);
        done += 1;
        setSessions((cur) =>
          cur
            ? cur.map((x) => {
                if (x.share !== share) return x;
                const { assignment, late } = assignmentOf(v.assignment?.key, assignments, x.last_event_at);
                return { ...x, assignment, late };
              })
            : cur,
        );
      } catch (err) {
        setError(errText(err));
      }
    }
    setBulkSaid(phrases.assignmentSetN(done, shares.length));
    if (done === shares.length) mode.select(false);
  };

  // the rows, their counts and their order go by the state a session says
  // and the alerts that still stand (classroom.asShown); a watch keeps every
  // one its session raised
  // each as shown, kept for its card and its mark so a frame about one
  // session leaves every other's object, and its memoized row, as it was
  const shownOf = useRef(new WeakMap<ClassSession, { seen?: SeenMark; shown: ClassSession }>());
  const sorted = useMemo(() => {
    if (!sessions) return [];
    return sortSessions(
      sessions.map((s) => {
        const kept = shownOf.current.get(s);
        if (kept && kept.seen === seen[s.share]) return kept.shown;
        const shown = asShown(s, seen[s.share]);
        shownOf.current.set(s, { seen: seen[s.share], shown });
        return shown;
      }),
    );
  }, [sessions, seen]);
  // what is watched, as the feed keeps it, whatever the filters show, in
  // two places: the pinned, in the order they were pinned, and the one
  // picked last unless it is one of them; a share the list no longer has is
  // left out
  const find = useCallback((id: string) => sessions?.find((s) => s.share === id), [sessions]);
  const pinnedWatched = useMemo(() => pins.map(find).filter((s): s is ClassSession => !!s), [pins, find]);
  // the class deleted (the relay closes its feed with 4404, byj-56x): nothing
  // on the page acts on it any more, and no watch is opened again
  const deleted = conn.kind === "closed" && conn.reason === ClassDeletedReason;
  const picked = (!deleted && watching && !pins.includes(watching) && find(watching)) || null;
  // a pin the list no longer has is dropped, so it does not hold one of
  // MaxPins out of sight; the class deleted, every pin is, which closes the
  // pinned watches and their sockets on shares no longer of the class, and
  // the picked one and any picking go with them; what was marked seen goes
  // the same way
  useEffect(() => {
    if (deleted) {
      keepPins(() => false);
      keepSeen(() => false);
      setWatching(null);
      stopPicking(false);
    } else if (sessions) {
      const listed = (id: string) => sessions.some((s) => s.share === id);
      keepPins(listed);
      keepSeen(listed);
    }
    // keepPins and keepSeen are made anew each render: what decides is the list and the deletion
  }, [sessions, deleted]);
  const watchedIds = useMemo(() => new Set([...pinnedWatched.map((s) => s.share), ...(picked ? [picked.share] : [])]), [pinnedWatched, picked]);
  const shown = useMemo(() => sorted.filter((s) => matches(s, filter, query, assignment, alertFilter)), [sorted, filter, query, assignment, alertFilter]);
  const n = useMemo(() => counts(sorted), [sorted]);
  // how many sessions stand on each kind of alert, pinned or not
  const kinds = useMemo(() => kindCounts(sorted), [sorted]);
  // an alert filter no session answers any more lets go of the rows
  useEffect(() => {
    if (alertFilter === "any" ? n.alerts === 0 : alertFilter !== "" && !kinds.some(([k]) => k === alertFilter)) setAlertFilter("");
  }, [kinds, n.alerts, alertFilter]);
  const sum = useMemo(() => totals(sorted), [sorted]);
  const chips: Chip[] = (["all", ...States] as Filter[]).map((f) => ({
    value: f,
    label: f === "all" ? t("classes:filterAll") : (word("classes:stateBadge", f) ?? f),
    count: n[f],
  }));
  // the pinned first, apart, an alert of theirs said on their row; the rest
  // by what they want of the instructor; the search and the chips narrow
  // both (the watches stay)
  const pinnedRows = pins.map((id) => shown.find((s) => s.share === id)).filter((s): s is ClassSession => !!s);
  const unpinned = shown.filter((s) => !pins.includes(s.share));
  // what is picked among the rows in view: the search and the chips narrow it too
  const pickedShown = shown.map((x) => x.share).filter((id) => mode.picked.has(id));
  const narrowed = query.trim() !== "" || filter !== "all" || assignment !== "" || alertFilter !== "";
  const groups: Group<ClassSession>[] = [
    { title: t("classes:needsAttention"), items: unpinned.filter(wantsAttention), className: "rows-head-attention" },
    { title: t("shares:liveNow"), items: unpinned.filter((s) => s.live && s.state !== "ended" && !wantsAttention(s)), className: "rows-head-live" },
    { title: t("shares:earlier"), items: unpinned.filter((s) => !s.live || s.state === "ended") },
  ];
  // the watch grid's rows: the pinned zone's heading, its rows two by two, then the one picked's heading and it
  const pinnedRowsEnd = pinnedWatched.length > 0 ? 2 + Math.ceil(pinnedWatched.length / 2) : 1;
  const pickedHeadRow = pinnedRowsEnd;
  const watchOf = (w: ClassSession) => {
    const pinned = pins.includes(w.share);
    return (
      <SessionWatch
        key={w.share}
        session={w}
        me={me}
        pinned={pinned}
        canPin={pins.length < MaxPins}
        onPin={() => {
          togglePin(w.share);
          // unpinned, it stays watched as the one picked
          if (pinned) setWatching(w.share);
        }}
        onAssign={openAssignments.length > 0 || w.assignment ? () => void setShareAssignment(w) : undefined}
        alerts={currentAlerts(w, seen[w.share])}
        onSeen={() => {
          const mark = seenMark(w);
          if (mark) markSeen(w.share, mark);
        }}
        onClose={() => {
          if (pinned) togglePin(w.share);
          if (watching === w.share) setWatching(null);
        }}
        load={loadShare}
        socket={socket}
      />
    );
  };
  const classPath = `/c/${encodeURIComponent(cls.id)}`;

  return (
    <Page width="full">
      {chooseDialog}
      <PageHead
        title={cls.name}
        meta={
          <span className="contents" data-testid="totals">
            <span className="chip chip-live">{t("classes:liveCount", { n: sum.live })}</span>
            <span className="chip">{t("classes:endedCount", { n: sum.ended })}</span>
            <span className="muted">{t("classes:totalCost", { c: money(sum.cost) })}</span>
          </span>
        }
        actions={
          <>
            <Popover label={t("classes:messageAll")} button={t("classes:messageAll")} className="pop" popClassName="pop-panel" focus="textarea" disabled={deleted}>
              {() => <Broadcast classId={cls.id} announced={announced} onAnnounced={setAnnounced} />}
            </Popover>
            <Link to={`${classPath}/preview`} className="button">
              {t("classes:previewAsStudent")}
            </Link>
            <Link to={`${classPath}/manage`} className="button">
              {t("classes:manage")}
            </Link>
          </>
        }
      />
      {/* the open assignments, a chip each to its page: its title whole, and
          when it is due, briefly, in the warning's colour once it is near */}
      {openAssignments.length > 0 && (
        <ul className="assignment-chips" aria-label={t("classes:openAssignments")} data-testid="assignment-links">
          {openAssignments.map((a) => {
            const due = dueState(a, now);
            return (
              <li key={a.key} title={a.due_at ? `${a.title} · ${t("classes:dueOn", { at: whenMinute(a.due_at) })}` : a.title}>
                <Link to={`${classPath}/a/${encodeURIComponent(a.key)}`} className={`assignment-chip${due ? ` due-${due.tone}` : ""}`}>
                  <span className="assignment-chip-title">{a.title}</span>
                  {a.due_at && (
                    <>
                      {" "}
                      <span className="assignment-chip-due">{dueShort(a.due_at, new Date(now))}</span>
                    </>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {conn.kind === "waiting" && <p className="banner warn small">{t("session:reconnecting", { s: Math.ceil(conn.retryInMs / 1000) })}</p>}
      {conn.kind === "closed" && conn.reason === ClassDeletedReason && (
        <p className="banner warn small">
          {t("classes:classDeleted")} <Link to="/classes">{t("classes:classesTitle")}</Link>
        </p>
      )}
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={() => void reload()}>{t("common:retry")}</button>
        </p>
      )}

      {/* the filters, in two parts (byj-1wv.51.10): the state a session is in
          — what it is doing — then, past a rule, its alerts — what is to be
          looked at: any, then each kind by its sign and how many. A narrow
          screen gives the alerts a line of their own, the states' scrolling
          sideways */}
      <div className="panel-tools class-tools" role="toolbar" aria-label={t("session:filterSessions")}>
        <FilterChips label={t("classes:filterByState")} chips={chips} value={filter} onChange={(v) => setFilter(v as Filter)} />
        <div className="filter-chips row alert-filters" role="group" aria-label={t("session:filterByAlert")}>
          <button
            type="button"
            className={`chip filter-chip${alertFilter === "any" ? " picked" : ""}${n.alerts === 0 ? " filter-chip-empty" : ""}`}
            aria-pressed={alertFilter === "any"}
            disabled={n.alerts === 0}
            onClick={() => setAlertFilter(alertFilter === "any" ? "" : "any")}
          >
            {t("classes:filterAlerts")} <span className="filter-count">{n.alerts}</span>
          </button>
          {kinds.map(([k, count]) => (
            <button
              key={k}
              type="button"
              className={`chip filter-chip alert-kind tone-${alertTone(k)}${alertFilter === k ? " picked" : ""}`}
              aria-pressed={alertFilter === k}
              aria-label={t("session:alertKindCount", { kind: word("session:alertKind", k) ?? k, count })}
              title={word("session:alertKind", k) ?? k}
              onClick={() => setAlertFilter(alertFilter === k ? "" : k)}
            >
              <AlertIcon kind={k} /> {word("session:alertShort", k) ?? word("session:alertKind", k) ?? k} <span className="filter-count">{count}</span>
            </button>
          ))}
        </div>
      </div>

      {sessions === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : sessions.length === 0 ? (
        <p className="muted">{t("classes:noSessions")}</p>
      ) : (
        <div className={watchedIds.size > 0 ? "dashboard-split" : undefined}>
          {/* the search over the rows it narrows, kept when it matches none */}
          <div className="rows-panel" data-testid="session-list">
            {/* over the rows, what narrows them — the search, the assignment
                they are of and a way to its page — and, apart at the end, one
                assignment set to several of them picked; on a phone the
                search has a line of its own and the assignment's take the next */}
            <div className="list-find">
              <ListSearch value={query} onChange={setQuery} label={t("classes:searchStudents")} shown={shown.length} of={sorted.length} />
              {(assignments.length > 0 || openAssignments.length > 0) && (
                <div className="list-find-assignment">
                  {assignments.length > 0 && (
                    <select aria-label={t("classes:assignments")} value={assignment} onChange={(e) => setAssignment(e.target.value)}>
                      <option value="">{t("classes:assignmentFilterAll")}</option>
                      <option value={NoAssignment}>{t("classes:assignmentFilterNone")}</option>
                      {assignments.map((a) => (
                        <option key={a.key} value={a.key}>
                          {a.title} ({a.key}){a.archived_at ? ` · ${t("classes:archived")}` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  {assignment && assignment !== NoAssignment && (
                    <Link className="button small list-find-open" to={`${classPath}/a/${encodeURIComponent(assignment)}`}>
                      <span aria-hidden="true">↗</span>
                      <span className="sr-only">{t("classes:openAssignmentPage")}</span>
                    </Link>
                  )}
                  {openAssignments.length > 0 && (
                    <button type="button" className="small bulk-assign" aria-pressed={mode.selecting} disabled={deleted} onClick={() => mode.select(!mode.selecting)}>
                      {t("classes:bulkAssign")}
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* while sessions are picked for an assignment (Set assignment, at the
                end of the search's line): a banner over the rows, kept in view as they
                scroll, saying how far it has got, its action there from the
                start; after, what it did */}
            {mode.selecting ? (
              <div className="rows-head-line list-tools is-picking" data-testid="list-tools" role="group" aria-label={t("classes:bulkAssign")}>
                <span className="small" role="status" title={phrases.pickingStatus(pickedShown.length)}>
                  {phrases.pickingStatus(pickedShown.length)}
                </span>
                <SelectionBar ids={shown.map((x) => x.share)} picked={mode.picked} setAll={mode.setAll} />
                <button type="button" className="small primary" disabled={pickedShown.length === 0} onClick={() => void setPickedAssignment(pickedShown)}>
                  {pickedShown.length === 0 ? t("classes:pickSessions") : t("classes:setAssignmentForN", { n: pickedShown.length })}
                </button>
                <button type="button" className="small" onClick={() => mode.select(false)}>
                  {t("common:cancel")}
                </button>
              </div>
            ) : (
              bulkSaid && (
                <div className="rows-head-line list-tools" data-testid="list-tools">
                  <span className="muted small" role="status" title={bulkSaid}>
                    {bulkSaid}
                  </span>
                </div>
              )
            )}
            {narrowed && shown.length === 0 && <p className="muted">{t("classes:noMatch")}</p>}
            {pinnedRows.length > 0 && (
              <div className="pinned-rows" role="group" aria-label={t("classes:pinnedGroup")} data-testid="pinned-rows">
                <div className="rows-head-line">
                  <h3 className="rows-head rows-head-pinned">
                    {t("classes:pinnedGroup")} · {pinnedRows.length}
                  </h3>
                </div>
                <ul className="rows">
                  {pinnedRows.map((s) => (
                    <ClassSessionRow key={s.share} s={s} now={now} selected onSelect={press} picking={mode.selecting} picked={mode.picked.has(s.share)} onPick={mode.toggle} />
                  ))}
                </ul>
              </div>
            )}
            {unpinned.length > 0 && (
              <Grouped
                id="sessions"
                groups={groups}
                // under the pinned rows' heading, the rest keep theirs
                headed={pinnedRows.length > 0}
                row={(s) => (
                  <ClassSessionRow
                    key={s.share}
                    s={s}
                    now={now}
                    selected={watchedIds.has(s.share)}
                    onSelect={press}
                    picking={mode.selecting}
                    picked={mode.picked.has(s.share)}
                    onPick={mode.toggle}
                  />
                )}
              />
            )}
          </div>
          {watchedIds.size > 0 && (
            // every watch a cell of one grid, keyed, whatever zone it is in:
            // pinning one or unpinning moves it rather than mounting it again,
            // so its sockets, its screen, its timeline's folds and a note half
            // written stay. The zones — the pinned two by two, the one picked
            // the whole width under them — are where the cells are placed, a
            // frame and a heading laid over the same grid
            <div className={`watch-column${pinnedWatched.length > 0 ? " has-pins" : ""}`} data-testid="watch-column">
              <div className="watch-board">
                {pinnedWatched.length > 0 && (
                  <>
                    <div className="watch-zone watch-zone-pinned" aria-hidden="true" style={{ gridRow: `1 / ${pinnedRowsEnd}` }} />
                    <h3 className="rows-head rows-head-pinned watch-zone-head head-pinned" style={{ gridRow: 1 }}>
                      {t("classes:pinnedGroup")} · {pinnedWatched.length}/{MaxPins}
                    </h3>
                  </>
                )}
                {picked && (
                  <>
                    <div className="watch-zone watch-zone-picked" aria-hidden="true" style={{ gridRow: `${pickedHeadRow} / ${pickedHeadRow + 2}` }} />
                    <h3 className="rows-head watch-zone-head head-picked" style={{ gridRow: pickedHeadRow }}>
                      {t("classes:watchingZone")}
                    </h3>
                  </>
                )}
                {/* one keyed array, the picked last: React matches keys within an
                    array alone, so a picked watch in a sibling of its own would
                    be mounted again as it is pinned */}
                {[...pinnedWatched.map((w, i) => ({ w, pinned: true, i })), ...(picked ? [{ w: picked, pinned: false, i: 0 }] : [])].map(({ w, pinned, i }) => (
                  <div
                    key={w.share}
                    className="watch-cell"
                    role="group"
                    aria-label={pinned ? t("classes:pinnedGroup") : t("classes:watchingZone")}
                    data-zone={pinned ? "pinned" : "picked"}
                    style={pinned ? { gridRow: 2 + Math.floor(i / 2), gridColumn: (i % 2) + 1 } : { gridRow: pickedHeadRow + 1, gridColumn: "1 / -1" }}
                  >
                    {watchOf(w)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
